/**
 * Image Processing Utilities
 * Handles HEIC conversion, compression, and optimization for web upload
 * Optimized for iOS/iPad devices that commonly produce HEIC images
 */

import imageCompression from "browser-image-compression";
import heic2any from "heic2any";

export interface ImageProcessingOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  fileType?: string;
  quality?: number;
}

export interface ProcessedImage {
  file: File;
  url: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

/**
 * Check if a file is HEIC format
 */
export function isHEIC(file: File): boolean {
  const heicExtensions = [".heic", ".heif"];
  const fileName = file.name.toLowerCase();
  return (
    heicExtensions.some((ext) => fileName.endsWith(ext)) ||
    file.type === "image/heic" ||
    file.type === "image/heif"
  );
}

/**
 * Convert HEIC image to JPEG
 */
export async function convertHEICtoJPEG(file: File): Promise<File> {
  try {
    console.log("[ImageUtils] Converting HEIC to JPEG:", file.name);

    // Convert HEIC to JPEG blob
    const convertedBlob = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.9, // High quality for conversion
    });

    // Handle array result (heic2any can return array of blobs)
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

    // Create new File from blob
    const fileName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
    const convertedFile = new File([blob], fileName, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });

    console.log("[ImageUtils] HEIC conversion successful:", {
      original: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
      converted: `${(convertedFile.size / 1024 / 1024).toFixed(2)}MB`,
    });

    return convertedFile;
  } catch (error) {
    console.error("[ImageUtils] HEIC conversion failed:", error);
    throw new Error(
      `Failed to convert HEIC image: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

/**
 * Compress and optimize image for web upload
 * Target: Max 1MB per image, reasonable quality
 */
export async function compressImage(
  file: File,
  options: ImageProcessingOptions = {}
): Promise<File> {
  const defaultOptions: ImageProcessingOptions = {
    maxSizeMB: 1, // Target 1MB max per image
    maxWidthOrHeight: 1920, // Max dimension (good for tablets/phones)
    useWebWorker: true,
    fileType: "image/jpeg",
    quality: 0.85, // Good balance between quality and size
  };

  const finalOptions = { ...defaultOptions, ...options };

  try {
    console.log("[ImageUtils] Compressing image:", {
      name: file.name,
      originalSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
      target: `${finalOptions.maxSizeMB}MB`,
    });

    const compressedFile = await imageCompression(file, finalOptions);

    console.log("[ImageUtils] Compression complete:", {
      originalSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
      compressedSize: `${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`,
      ratio: `${((1 - compressedFile.size / file.size) * 100).toFixed(1)}% reduction`,
    });

    return compressedFile;
  } catch (error) {
    console.error("[ImageUtils] Compression failed:", error);
    throw new Error(
      `Failed to compress image: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

/**
 * Process image for upload: Handle HEIC conversion + compression
 * This is the main function to use for all image uploads
 */
export async function processImageForUpload(
  file: File,
  options: ImageProcessingOptions = {}
): Promise<ProcessedImage> {
  const originalSize = file.size;
  let processedFile = file;

  try {
    // Step 1: Convert HEIC to JPEG if needed
    if (isHEIC(file)) {
      console.log("[ImageUtils] Detected HEIC image, converting to JPEG...");
      processedFile = await convertHEICtoJPEG(file);
    }

    // Step 2: Compress the image
    processedFile = await compressImage(processedFile, options);

    // Step 3: Create object URL for preview
    const url = URL.createObjectURL(processedFile);

    const result: ProcessedImage = {
      file: processedFile,
      url,
      originalSize,
      compressedSize: processedFile.size,
      compressionRatio: (1 - processedFile.size / originalSize) * 100,
    };

    console.log("[ImageUtils] Image processing complete:", {
      original: `${(originalSize / 1024 / 1024).toFixed(2)}MB`,
      final: `${(processedFile.size / 1024 / 1024).toFixed(2)}MB`,
      savings: `${result.compressionRatio.toFixed(1)}%`,
    });

    return result;
  } catch (error) {
    console.error("[ImageUtils] Image processing failed:", error);
    throw new Error(
      `Failed to process image: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

/**
 * Process multiple images in parallel
 */
export async function processImagesForUpload(
  files: File[],
  options: ImageProcessingOptions = {}
): Promise<ProcessedImage[]> {
  console.log(`[ImageUtils] Processing ${files.length} images...`);

  try {
    const results = await Promise.all(files.map((file) => processImageForUpload(file, options)));

    const totalOriginal = results.reduce((sum, r) => sum + r.originalSize, 0);
    const totalCompressed = results.reduce((sum, r) => sum + r.compressedSize, 0);

    console.log("[ImageUtils] Batch processing complete:", {
      count: results.length,
      originalTotal: `${(totalOriginal / 1024 / 1024).toFixed(2)}MB`,
      compressedTotal: `${(totalCompressed / 1024 / 1024).toFixed(2)}MB`,
      averageSavings: `${((1 - totalCompressed / totalOriginal) * 100).toFixed(1)}%`,
    });

    return results;
  } catch (error) {
    console.error("[ImageUtils] Batch processing failed:", error);
    throw error;
  }
}

/**
 * Validate image file type and size
 */
export function validateImageFile(file: File, maxSizeMB = 10): { valid: boolean; error?: string } {
  // Check if it's an image
  const validTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/heic",
    "image/heif",
  ];
  const isValidExtension = /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(file.name);

  if (!(validTypes.includes(file.type) || isValidExtension)) {
    return {
      valid: false,
      error: "Invalid file type. Please upload an image (JPG, PNG, GIF, WebP, or HEIC).",
    };
  }

  // Check file size (before compression)
  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `File is too large. Maximum size is ${maxSizeMB}MB before compression.`,
    };
  }

  return { valid: true };
}

/**
 * Clean up object URLs to prevent memory leaks
 */
export function revokeImageURL(url: string): void {
  if (url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}

/**
 * Upload processed image to R2 storage via API
 */
export async function uploadImageToR2(
  file: File,
  projectId: string,
  apiUrl: string = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788"
): Promise<{ url: string; key: string }> {
  try {
    console.log("[ImageUtils] Uploading image to R2:", file.name);

    // Create form data
    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", projectId);
    formData.append("fileName", file.name);

    // Upload to API
    const response = await fetch(`${apiUrl}/api/v1/images/upload`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Upload failed: ${response.statusText}`);
    }

    const data = await response.json();

    console.log("[ImageUtils] Upload successful:", data.data);

    return {
      url: data.data.url,
      key: data.data.key,
    };
  } catch (error) {
    console.error("[ImageUtils] Upload failed:", error);
    throw new Error(
      `Failed to upload image: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
